import { Component } from '@theme/component';
import { trapFocus, removeTrapFocus } from '@theme/focus';
import { onAnimationEnd, removeWillChangeOnAnimationEnd } from '@theme/utilities';

/*
 * ESTHEA (ESL-503, 1/10/2026): lo que se desvía de Horizon 4.1.5, para la próxima actualización del tema.
 * Al actualizar, se vuelve a aplicar sobre el fichero nuevo, o se borra si Horizon ya lo resuelve: con el
 * submenú abierto, el Tab que sigue al último enlace no tiene que volver a la fila del primer nivel.
 * Son tres cambios, todos marcados «ESTHEA (ESL-503)», y sólo afectan a los submenús del cajón (hoy,
 * sólo el de Tienda): el cajón entero (`this.refs.details`) sigue como en Horizon.
 *  1. open(): la trampa de foco de un submenú es su panel (`.menu-drawer__submenu`), no el `details`
 *     entero. Horizon atrapaba el details, que incluye su summary (la fila «Tienda» del primer nivel), y
 *     esa fila queda tapada por el panel: el Tab que seguía a «Todos nuestros productos» llevaba el foco
 *     ahí, sin verse, e Intro cerraba el submenú (WCAG 2.4.11 y 2.4.3; ACC-07 del panel, ronda
 *     261001-163428). Con el panel, el Tab que sigue al último vuelve a «Volver al menú principal», y
 *     Mayús+Tab desde ahí va al último.
 *  2. open(): si el submenú se abre con el teclado (la fila tenía `:focus-visible`), el foco pasa a
 *     «Volver al menú principal». En Horizon se quedaba en la fila, ya tapada. Con el dedo o el ratón
 *     no se mueve nada a la vista: trapFocus enfoca el panel, que lleva tabindex="-1" de fábrica.
 *  3. #close(): al volver de un submenú con el teclado («<» o Escape), el foco regresa a su fila del
 *     primer nivel. En Horizon se quedaba en el botón de volver, dentro del panel cerrado, y caía al body.
 *
 * ESTHEA (ESL-784, 17/10/2026): cuatro cambios más, marcados «ESTHEA (ESL-784)», sobre el cajón entero.
 *  4. Escape: cierra el nivel abierto más interior y, si no queda ninguno, el cajón. En Horizon el Escape
 *     buscaba el details más cercano al foco; con el foco en la fila «Tienda» después de cerrar su
 *     submenú, ese details ya estaba cerrado y el segundo Escape no hacía nada. Se escucha en el
 *     documento mientras el cajón está abierto, también si el foco no está dentro.
 *  5. La trampa de foco del cajón es su panel (`menuDrawer`), no el details: el botón «Menú» es el
 *     summary del details y queda tapado por el panel; el Tab pasaba por él sin verse.
 *  6. Al cerrar el cajón, el foco vuelve al botón «Menú» (en Horizon caía en el body). Si se abre con
 *     el teclado, el foco entra en el cajón, en su primer control («Cerrar»).
 *  7. Mientras el cajón está abierto, todo lo que queda fuera de él lleva `inert`: no se llega con el
 *     teclado ni lo lee un lector de pantalla. Se quita al cerrar, sólo de lo que se marcó al abrir.
 */

/**
 * A custom element that manages the main menu drawer.
 *
 * @typedef {object} Refs
 * @property {HTMLDetailsElement} details - The details element.
 * @property {HTMLDivElement} menuDrawer - The slideable drawer panel containing the menu.
 *
 * @extends {Component<Refs>}
 */
class HeaderDrawer extends Component {
  requiredRefs = ['details', 'menuDrawer'];

  connectedCallback() {
    super.connectedCallback();

    // ESTHEA (ESL-784): en el documento, para que el Escape llegue aunque el foco no esté en el cajón
    document.addEventListener('keyup', this.#onKeyUp);
    this.#setupAnimatedElementListeners();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener('keyup', this.#onKeyUp);
    this.#quitarInert();
  }

  /**
   * ESTHEA (ESL-784): lo que se marcó con `inert` al abrir el cajón, para quitárselo al cerrar
   * @type {HTMLElement[]}
   */
  #inertes = [];

  /**
   * ESTHEA (ESL-784): marca con `inert` todo lo que queda fuera del cajón: los hermanos de cada
   * antepasado del details, hasta el body. Lo que ya era inerte se deja como estaba.
   */
  #ponerInert() {
    this.#quitarInert();
    /** @type {Element | null} */
    let nodo = this.refs.details;
    while (nodo && nodo !== document.body) {
      const padre = nodo.parentElement;
      if (!padre) break;
      for (const hermano of padre.children) {
        if (hermano === nodo || !(hermano instanceof HTMLElement) || hermano.inert) continue;
        if (hermano.matches('script, style, link, template, meta')) continue;
        hermano.inert = true;
        this.#inertes.push(hermano);
      }
      nodo = padre;
    }
  }

  /** ESTHEA (ESL-784): quita el `inert` que puso #ponerInert */
  #quitarInert() {
    for (const elemento of this.#inertes) elemento.inert = false;
    this.#inertes = [];
  }

  /**
   * Close the main menu drawer when the Escape key is pressed
   * ESTHEA (ESL-784): cierra el nivel abierto más interior (un submenú con `menu-open`) y, si no
   * hay ninguno, el cajón entero.
   * @param {KeyboardEvent} event
   */
  #onKeyUp = (event) => {
    if (event.key !== 'Escape' || !this.isOpen) return;

    const objetivo = event.target instanceof Element ? event.target : null;
    let submenu = objetivo?.closest('details.menu-open');
    if (!(submenu instanceof HTMLDetailsElement) || submenu === this.refs.details || !this.contains(submenu)) {
      submenu = this.refs.details.querySelector('details.menu-open');
    }
    if (submenu instanceof HTMLDetailsElement && submenu !== this.refs.details) {
      this.#close(submenu, true);
    } else {
      this.#close(this.refs.details, true);
    }
  };

  /**
   * @returns {boolean} Whether the main menu drawer is open
   */
  get isOpen() {
    return this.refs.details.hasAttribute('open');
  }

  /**
   * Get the closest details element to the event target
   * @param {Event | undefined} event
   * @returns {HTMLDetailsElement}
   */
  #getDetailsElement(event) {
    if (!(event?.target instanceof Element)) return this.refs.details;

    return event.target.closest('details') ?? this.refs.details;
  }

  /**
   * Toggle the main menu drawer
   */
  toggle() {
    return this.isOpen ? this.close() : this.open();
  }

  /**
   * Open the closest drawer or the main menu drawer
   * @param {string} [target]
   * @param {Event} [event]
   */
  open(target, event) {
    const details = this.#getDetailsElement(event);
    const summary = details.querySelector('summary');

    if (!summary) return;

    summary.setAttribute('aria-expanded', 'true');
    // ESTHEA (ESL-503): si se ha abierto con el teclado, el foco pasa al submenú (ver abajo)
    const teclado = summary.matches(':focus-visible');

    this.preventInitialAccordionAnimations(details);
    // ESTHEA (ESL-784): con el cajón abierto, lo de fuera, inerte
    if (details === this.refs.details) this.#ponerInert();
    requestAnimationFrame(() => {
      details.classList.add('menu-open');

      if (target) {
        this.refs.menuDrawer.classList.add('menu-drawer--has-submenu-opened');
      }

      // Wait for the drawer animation to complete before trapping focus
      const drawer = details.querySelector('.menu-drawer, .menu-drawer__submenu');
      // ESTHEA (ESL-503): en un submenú, la trampa es su panel y no el details entero, cuyo
      // summary queda tapado por el panel. Con el teclado, el foco va a «Volver al menú principal».
      // ESTHEA (ESL-784): en el cajón, la trampa es su panel (menuDrawer), sin el botón «Menú»,
      // y con el teclado el foco entra en su primer control.
      const trampa = target && drawer instanceof HTMLElement ? drawer : this.refs.menuDrawer;
      onAnimationEnd(
        drawer || details,
        () => {
          trapFocus(trampa);
          if (target && teclado) {
            trampa.querySelector('.menu-drawer__back-button')?.focus();
          } else if (!target && teclado) {
            const primero = trampa.querySelector('button:enabled, a[href], summary');
            if (primero instanceof HTMLElement) primero.focus();
          }
        },
        { subtree: false }
      );
    });
  }

  /**
   * Go back or close the main menu drawer
   * @param {Event} [event]
   */
  back(event) {
    this.#close(this.#getDetailsElement(event));
  }

  /**
   * Close the main menu drawer
   */
  close() {
    this.#close(this.refs.details);
  }

  /**
   * Close the closest menu or submenu that is open
   *
   * @param {HTMLDetailsElement} details
   * @param {boolean} [conEscape] - ESTHEA (ESL-784): se cierra con la tecla Escape
   */
  #close(details, conEscape = false) {
    const summary = details.querySelector('summary');

    if (!summary) return;

    // ESTHEA (ESL-503): al volver de un submenú con el teclado, el foco regresa a su fila
    // ESTHEA (ESL-784): también con Escape, y al cerrar el cajón el foco vuelve al botón «Menú»
    // si estaba dentro del cajón o en ninguna parte (el body).
    const activo = document.activeElement;
    const esCajon = details === this.refs.details;
    const devolver = esCajon
      ? !activo || activo === document.body || this.refs.menuDrawer.contains(activo) || activo === summary
      : activo instanceof Element && details.contains(activo) && (conEscape || activo.matches(':focus-visible'));
    if (esCajon) this.#quitarInert();

    summary.setAttribute('aria-expanded', 'false');
    details.classList.remove('menu-open');
    this.refs.menuDrawer.classList.remove('menu-drawer--has-submenu-opened');

    // Wait for the .menu-drawer element's transition, not the entire details subtree
    // This avoids waiting for child accordion/resource-card animations which can cause issues on Firefox
    const drawer = details.querySelector('.menu-drawer, .menu-drawer__submenu');

    onAnimationEnd(
      drawer || details,
      () => {
        reset(details);
        if (details === this.refs.details) {
          removeTrapFocus();
          const openDetails = this.querySelectorAll('details[open]:not(accordion-custom > details)');
          openDetails.forEach(reset);
          // ESTHEA (ESL-784): el foco, al botón «Menú»
          if (devolver) summary.focus();
        } else {
          // ESTHEA (ESL-784): la trampa vuelve al panel del cajón, no al details con el botón «Menú»
          trapFocus(this.refs.menuDrawer);
          if (devolver) summary.focus();
        }
      },
      { subtree: false }
    );
  }

  /**
   * Attach animationend event listeners to all animated elements to remove will-change after animation
   * to remove the stacking context and allow submenus to be positioned correctly
   */
  #setupAnimatedElementListeners() {
    const allAnimated = this.querySelectorAll('.menu-drawer__animated-element');
    allAnimated.forEach((element) => {
      element.addEventListener('animationend', removeWillChangeOnAnimationEnd);
    });
  }

  /**
   * Temporarily disables accordion animations to prevent unwanted transitions when the drawer opens.
   * Adds a no-animation class to accordion content elements, then removes it after 100ms to
   * re-enable animations for user interactions.
   * @param {HTMLDetailsElement} details - The details element containing the accordions
   */
  preventInitialAccordionAnimations(details) {
    const content = details.querySelectorAll('accordion-custom .details-content');

    content.forEach((element) => {
      if (element instanceof HTMLElement) {
        element.classList.add('details-content--no-animation');
      }
    });
    setTimeout(() => {
      content.forEach((element) => {
        if (element instanceof HTMLElement) {
          element.classList.remove('details-content--no-animation');
        }
      });
    }, 100);
  }
}

if (!customElements.get('header-drawer')) {
  customElements.define('header-drawer', HeaderDrawer);
}

/**
 * Reset an open details element to its original state
 *
 * @param {HTMLDetailsElement} element
 */
function reset(element) {
  element.classList.remove('menu-open');
  element.removeAttribute('open');
  element.querySelector('summary')?.setAttribute('aria-expanded', 'false');
}
